/**
 * The work-order queue's column order — TRBUD-391.
 *
 * Eden, 27 and 30 October 2026: layered, in the order the person clicks; Reset
 * sort removes the order and keeps the filters. Eden, 30 October 2026 (after the
 * first preview), what each column does:
 *
 * | Column        | What it does                                                        |
 * | ------------- | ------------------------------------------------------------------- |
 * | Work order    | sorts by number, lowest first, then highest first                   |
 * | Issue         | nothing: it is not sortable                                          |
 * | Last update   | sorts by time                                                        |
 * | Pipeline      | groups by value (not a sort)                                         |
 * | Stage         | 1 click sorts, 2 clicks sort the other way, 3 clicks group by stage  |
 * | Assigned to   | groups by value (not a sort)                                         |
 * | Due           | sorts by date                                                        |
 * | Priority      | 1 click sorts, 2 clicks sort the other way, 3 clicks group           |
 *
 * ── A tier is a column and a MODE ────────────────────────────────────
 * `asc` and `desc` only ORDER the rows. `group` orders them by the same value and
 * also prints a heading wherever the value changes. A column that is only ever a
 * group (Pipeline, Assigned to) has the one mode. The first tier decides the
 * order, each later one only breaks ties in the ones before it, so the click
 * order IS the precedence and nobody has to invent how two columns combine.
 * Equal rows then fall back to the queue's own row order (overdue, priority,
 * oldest, number) and last to the number and id, so the order is total and a page
 * boundary can never repeat or lose a row.
 *
 * ── No order at all is the default order ─────────────────────────────
 * Asset bands, best priority first, then earliest due date. An empty list of
 * tiers is not a sort, it is today's queue, and Reset sort is "remove every tier".
 *
 * ── Why this lives here and not in the API ───────────────────────────
 * The URL, the request, the header buttons, the touch buttons and the group
 * headings all speak this one vocabulary. The SQL for each key stays in the API,
 * which owns the database, and a test holds the two together.
 *
 * Browser-safe: strings, a parser and pure functions. No `node:` imports.
 */
import { z } from "zod";
import type { Priority } from "./vocabulary.js";
import type { QueueRow } from "./wire.js";

/**
 * The columns that can be ordered, in the order the columns sit on the desktop
 * head. The touch buttons list them in this same order, left to right.
 */
export const queueSortKeys = ["workOrder", "lastUpdate", "pipeline", "stage", "assigned", "due", "priority"] as const;
export type QueueSortKey = (typeof queueSortKeys)[number];

export const queueSortModes = ["asc", "desc", "group"] as const;
export type QueueSortMode = (typeof queueSortModes)[number];

export interface QueueSortTier {
  key: QueueSortKey;
  mode: QueueSortMode;
}

/**
 * Rows per page while an order is active. The default list pages WHOLE asset
 * groups (25), which has no meaning once the asset bands are gone, so an ordered
 * page is a count of rows.
 */
export const QUEUE_SORT_PAGE_SIZE = 50;

/**
 * What each click on a column does, in order: the first mode a click gives, then
 * the next, and one more click after the last takes the column out of the order.
 *
 * - `asc` is the start of the column's scale: lowest number, oldest, earliest
 *   date, earliest stage, and for Priority the STRONGEST first (urgent, high,
 *   normal, low), the way `priorityRank` counts it.
 * - Last update starts newest first, which is what "last update" means to a reader.
 * - A column with only `group` is a one-click switch.
 */
export const queueSortCycle: Record<QueueSortKey, readonly QueueSortMode[]> = {
  workOrder: ["asc", "desc"],
  lastUpdate: ["desc", "asc"],
  pipeline: ["group"],
  stage: ["asc", "desc", "group"],
  assigned: ["group"],
  due: ["asc", "desc"],
  priority: ["asc", "desc", "group"],
};

/**
 * The words for each column and each mode it has. `label` is what the touch
 * button and the sentence under the list call it; the mode words say what that
 * mode does, for the tooltip, the screen reader and the footnote.
 */
export const queueSortLabels: Record<QueueSortKey, { label: string; modes: Partial<Record<QueueSortMode, string>> }> = {
  workOrder: { label: "Work order", modes: { asc: "Lowest number first", desc: "Highest number first" } },
  lastUpdate: { label: "Last update", modes: { asc: "Oldest first", desc: "Newest first" } },
  pipeline: { label: "Pipeline", modes: { group: "Grouped by pipeline and vendor" } },
  stage: { label: "Stage", modes: { asc: "Earliest stage first", desc: "Latest stage first", group: "Grouped by stage" } },
  assigned: { label: "Assigned to", modes: { group: "Grouped by person" } },
  due: { label: "Due", modes: { asc: "Earliest first", desc: "Latest first" } },
  priority: { label: "Priority", modes: { asc: "Strongest first", desc: "Weakest first", group: "Grouped by priority" } },
};

/**
 * The mark after a column's name for each mode: up for the start of the scale,
 * down for the other way, three bars for grouped. The desktop head draws them
 * from CSS and the touch button prints them in words, and a web test holds the
 * stylesheet to this table so the two cannot drift.
 */
export const QUEUE_SORT_MARKS: Record<QueueSortMode, string> = { asc: "↑", desc: "↓", group: "≡" };

/** The most tiers an order can hold: each column can be used once. */
export const QUEUE_SORT_MAX_TIERS = queueSortKeys.length;

function isKey(value: string): value is QueueSortKey {
  return (queueSortKeys as readonly string[]).includes(value);
}

function isMode(value: string): value is QueueSortMode {
  return (queueSortModes as readonly string[]).includes(value);
}

/** The mode a first click gives. */
export function queueSortFirstMode(key: QueueSortKey): QueueSortMode {
  return queueSortCycle[key][0]!;
}

/**
 * Reads `lastUpdate:desc,priority:group`, or `due` for the first mode.
 *
 * Returns null for anything unusable — an unknown column, a mode the column does
 * not have (`pipeline:asc`: Pipeline only groups), a column used twice or more
 * tiers than there are columns — and never a partial list: half of an order is a
 * different order. An empty string is no order, `[]`, not a failure.
 */
export function parseQueueSort(text: string | null | undefined): QueueSortTier[] | null {
  const trimmed = (text ?? "").trim();
  if (trimmed === "") return [];
  const tiers: QueueSortTier[] = [];
  for (const part of trimmed.split(",")) {
    const [key = "", mode, ...rest] = part.trim().split(":");
    if (rest.length > 0 || !isKey(key) || (mode !== undefined && !isMode(mode))) return null;
    if (tiers.some((tier) => tier.key === key)) return null;
    const resolved = mode ?? queueSortFirstMode(key);
    if (!queueSortCycle[key].includes(resolved)) return null;
    tiers.push({ key, mode: resolved });
  }
  return tiers.length > QUEUE_SORT_MAX_TIERS ? null : tiers;
}

/** The URL and request spelling. Always names the mode, so a link never depends on a default. */
export function formatQueueSort(tiers: readonly QueueSortTier[]): string {
  return tiers.map((tier) => `${tier.key}:${tier.mode}`).join(",");
}

/**
 * What one click on a column does:
 *
 * - not in the order yet: it becomes the NEXT tier, in the column's first mode;
 * - already in it: it moves to the column's next mode, keeping its place;
 * - on the column's last mode: it leaves the order.
 *
 * "Reset sort" removes every tier at once. Because a click is the only gesture,
 * a column needs no modifier key, which a finger on an iPad does not have.
 */
export function toggleQueueSortTier(tiers: readonly QueueSortTier[], key: QueueSortKey): QueueSortTier[] {
  const at = tiers.findIndex((tier) => tier.key === key);
  if (at === -1) return [...tiers, { key, mode: queueSortFirstMode(key) }];
  const cycle = queueSortCycle[key];
  const next = cycle[cycle.indexOf(tiers[at]!.mode) + 1];
  if (next === undefined) return tiers.filter((_, index) => index !== at);
  return tiers.map((tier, index) => (index === at ? { key, mode: next } : tier));
}

/** The words for a tier's mode: "Last update: Newest first". */
export function describeQueueSortTier(tier: QueueSortTier): string {
  const words = queueSortLabels[tier.key];
  return `${words.label}: ${words.modes[tier.mode] ?? tier.mode}`;
}

/**
 * The whole order on one line, for the touch view's Sort button:
 * `Pipeline ≡ › Stage ↑ › Priority ≡`, or `Default order` when none is chosen.
 */
export function describeQueueSortSummary(tiers: readonly QueueSortTier[]): string {
  if (tiers.length === 0) return "Default order";
  return tiers.map((tier) => `${queueSortLabels[tier.key].label} ${QUEUE_SORT_MARKS[tier.mode]}`).join(" › ");
}

/**
 * What the NEXT click on a column will do, for its tooltip.
 */
export function describeQueueSortNextClick(tiers: readonly QueueSortTier[], key: QueueSortKey): string {
  const words = queueSortLabels[key];
  const tier = tiers.find((candidate) => candidate.key === key);
  const cycle = queueSortCycle[key];
  const next = tier ? cycle[cycle.indexOf(tier.mode) + 1] : cycle[0];
  if (next === undefined) return `Stop ordering by ${words.label.toLowerCase()}`;
  const doing = next === "group" ? "Group by" : tier ? "Switch" : "Sort by";
  return `${doing} ${words.label.toLowerCase()}: ${words.modes[next]}`;
}

/**
 * `aria-sort` for a column header. A grouping is neither ascending nor
 * descending, which is what `other` is for.
 */
export function ariaSortOf(tiers: readonly QueueSortTier[], key: QueueSortKey): "ascending" | "descending" | "other" | "none" {
  const tier = tiers.find((candidate) => candidate.key === key);
  if (!tier) return "none";
  return tier.mode === "asc" ? "ascending" : tier.mode === "desc" ? "descending" : "other";
}

/**
 * The request parameter, for `GET /work-orders` and `/work-orders/grouped`.
 *
 * A string, like `categoryIds`: `buildUrl` in the web client sets a parameter
 * only for a string, number or boolean, and drops an array without a word. The
 * parsed value is the list of tiers.
 */
export const queueSortParam = z
  .string()
  .trim()
  .min(1)
  .max(160)
  .transform((value, ctx) => {
    const tiers = parseQueueSort(value);
    if (tiers === null || tiers.length === 0) {
      ctx.addIssue({
        code: "custom",
        message:
          `sort is a comma-separated list of ${queueSortKeys.join(", ")}, each optionally followed by ` +
          ":asc, :desc or :group (pipeline and assigned only group), with no column used twice",
      });
      return z.NEVER;
    }
    return tiers;
  });

/* ── group headings ────────────────────────────────────────────────── */

const PIPELINE_WORDS: Record<QueueRow["pipelineKind"], string> = { INTERNAL: "In-house", VENDOR: "Vendor" };
const PRIORITY_WORDS: Record<Priority, string> = { urgent: "Urgent", high: "High", normal: "Normal", low: "Low" };

/**
 * The group a row sits in, as the words of a heading, or `[]` for no heading.
 *
 * Headings come from the LEADING tiers that are in `group` mode. The first tier
 * that only orders (Last update, Due, or Priority and Stage in `asc` or `desc`)
 * ends them, because from there on the rows are in an order no heading describes.
 * So Priority grouped, then Due, prints Urgent, High, Normal, Low with dates
 * inside each, while Due, then Priority grouped, prints no headings at all.
 *
 * What each grouped column prints is exactly what the SQL sorts on, so a heading
 * changes where the sort changes group and nowhere else:
 *
 * - Pipeline: In-house or Vendor, then the vendor's name on vendor work. An
 *   in-house job with a parts supplier on it is not grouped under the supplier.
 * - Assigned to: the person, or "Unassigned".
 * - Stage: the stage's name.
 * - Priority: Urgent, High, Normal or Low.
 *
 * Counts are deliberately not part of a heading: a count computed here would be a
 * second figure to disagree with the counts strip.
 */
export function queueSortGroupPath(tiers: readonly QueueSortTier[], row: QueueRow): string[] {
  const path: string[] = [];
  for (const tier of tiers) {
    if (tier.mode !== "group") break;
    switch (tier.key) {
      case "pipeline":
        path.push(PIPELINE_WORDS[row.pipelineKind]);
        if (row.pipelineKind === "VENDOR") path.push(row.vendorName ?? "No vendor yet");
        break;
      case "assigned":
        path.push(row.assignee?.label ?? "Unassigned");
        break;
      case "stage":
        path.push(row.stage.label);
        break;
      case "priority":
        path.push(PRIORITY_WORDS[row.priority] ?? row.priority);
        break;
      default:
        // Work order, Last update and Due have no `group` mode, so no parsed
        // order reaches here; a hand-built one prints nothing for them.
        break;
    }
  }
  return path;
}
